/**
 * ISO 3166-1 alpha-2 country options for a given locale.
 *
 * A country field is a closed ~250-value set, so it is served from here rather
 * than from the address provider — that alone was 26% of billed suggest traffic
 * (ENG-4719). `Intl.supportedValuesOf('region')` isn't universally supported
 * (older Safari / Node), so the canonical set ships here; `Intl.DisplayNames`
 * turns each code into a label for the active locale, so nothing is hand-translated.
 *
 * Mirrors `frontends/apps/tenant-frontend/src/fnol/country-options.ts`; the two
 * frontends share no package, so the data is duplicated rather than hoisted.
 */

export interface CountryOption {
  value: string;
  label: string;
}

/** Per-locale cache so the (~250-entry) list isn't rebuilt on every render. */
const cache = new Map<string, CountryOption[]>();
const codeCache = new Map<string, CountryOption[]>();

/**
 * Canonical ISO 3166-1 alpha-2 country codes. Source: the ISO Online Browsing
 * Platform — see https://www.iso.org/obp/ui/#search/code/. Includes both
 * sovereign states and dependencies (per ISO).
 */
const ISO_3166_1_ALPHA_2: readonly string[] = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
];

/** The display name a country field stores, for one alpha-2 code. */
export function getCountryLabel(code: string | undefined, locale: string): string | undefined {
  if (!code) {
    return undefined;
  }
  const label = new Intl.DisplayNames([locale], { type: 'region' }).of(code);
  return label === code ? undefined : label;
}

/**
 * Options for a country picker. `value` is the localized display name, not the
 * code: folio persists country fields as display names and every issued policy
 * already holds one.
 */
export function getCountryOptions(locale: string): CountryOption[] {
  const cached = cache.get(locale);
  if (cached) {
    return cached;
  }

  const display = new Intl.DisplayNames([locale], { type: 'region' });
  const options = ISO_3166_1_ALPHA_2.map(code => ({
    value: display.of(code) ?? code,
    label: display.of(code) ?? code,
  })).sort((a, b) => a.label.localeCompare(b.label, locale));

  cache.set(locale, options);
  return options;
}

/** Options whose `value` is the alpha-2 code, for contracts that store the code (claim fields). */
export function getCountryCodeOptions(locale: string): CountryOption[] {
  const cached = codeCache.get(locale);
  if (cached) {
    return cached;
  }

  const display = new Intl.DisplayNames([locale], { type: 'region' });
  const options = ISO_3166_1_ALPHA_2.map(code => ({
    value: code,
    label: display.of(code) ?? code,
  })).sort((a, b) => a.label.localeCompare(b.label, locale));

  codeCache.set(locale, options);
  return options;
}
